import { Component, createRef } from 'preact';
import { motion } from 'framer-motion';
import DeckCardTile, { getImageUrl } from './DeckCardTile';
import CardInspector from './CardInspector';
import CardFilterBar from './CardFilterBar';
import CardOwnedBadge from './CardOwnedBadge';
import CardFlipButton from './CardFlipButton';
import AlphabetScrollIndex from './AlphabetScrollIndex';
import CardGrid from './CardGrid';
import CardSortChips from './CardSortChips';
import { isFoilFinish, FOIL_LABEL } from '../utils/sorcery/foil';
import {
  ART_GROUP_BASE, artGroupOf, artGroupsForCard, printingsInArtGroup,
  artGroupLabel, artGroupDisplayName,
} from '../utils/sorcery/artGroups';
import { foilZonesForCard } from '../utils/sorcery/foilZones';
import { getMaxCopies, getRemainingCopies, isCardBannedByAvatar } from '../utils/sorcery/deckRules';
import { resolveAvatarRules } from '../utils/game/avatarRules';
import { buildVariantOwnedMap, variantKey, defaultCardScope } from '../utils/arena/collectionUtils';
import { isFreePlay } from '../utils/freePlay';
import { playUI, UI } from '../utils/arena/uiSounds';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { getPref, setPref } from '../utils/persistentPrefs';
import {
  CARD_SORT_PREF_KEY, CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC,
  normalizeCardSort, normalizeCardSortDir, makeCardComparator, nextCardSort,
} from '../utils/sorcery/cardSort';
import { TEXT_MUTED, ACCENT_GOLD, GOLD } from '../lib/medievalTheme';
import { isPhoneLayout } from '../utils/platform';
import { t, k } from '../localization/t';

// Session-constant (see platform.js). On phone: rem-sized footer chrome,
// no Cols selector (grid columns are fixed by CSS), no alphabet rail.
const PHONE = isPhoneLayout();

// Identity of one grid tile: a card in one illustration in one finish. Every
// per-tile bit of state (flip, alphabet highlight, flash, inspector) is keyed
// by this, so it has to carry all three axes.
const entryKey = (cardId, artGroup, foiling) =>
  `${cardId}-${artGroup || ART_GROUP_BASE}-${foiling || 'S'}`;

// Tiles the first viewport can hold at the widest grid size. Preloading past
// this buys nothing. The rest scroll in behind the user, covered by their own
// per-tile placeholder. Phone viewports hold far fewer tiles.
const PRELOAD_TILE_COUNT = PHONE ? 12 : 30;

// Ceiling on the open gate. A cold image cache (first run, or a set the player
// has never browsed) can take arbitrarily long, and the editor opening late is
// worse than it opening with a few placeholders. Past this the entrance runs
// regardless. Which is exactly the pre-gate behaviour, so the timeout degrades
// to the old experience rather than to a broken one.
const PRELOAD_TIMEOUT_MS = 3000;

/**
 * Resolve once every URL has loaded or failed, or the deadline passes.
 * `onload` is deliberately the same readiness condition DeckCardTile keys its
 * own `imgLoaded` off, so a tile that this resolved for is a tile that paints
 * its art on the first frame of the entrance cascade instead of a placeholder.
 */
function preloadImages(urls, timeoutMs) {
  if (!urls.length) return Promise.resolve();
  const loaded = Promise.all(urls.map((url) => new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = url;
  })));
  return Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
}

const GRID_SIZE_PREF_KEY = 'valkenhall:deck-grid-size';
const GRID_SIZE_OPTIONS = [
  { value: 'auto', label: k('Auto') },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
];

function normalizeGridSize(raw) {
  if (raw === 'auto') return 'auto';
  const n = Number(raw);
  return n === 4 || n === 5 || n === 6 ? n : 'auto';
}

export default class DeckEditorCollection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Filter predicate + raw state come from the CardFilterBar child.
      // Defaults pass everything until the first onFilterChange fires
      // (which happens synchronously on mount inside componentDidMount).
      matchesCard: () => true,
      matchesFoiling: () => true,
      filterScope: defaultCardScope(props.chosenCards),
      hoveredCard: null,
      inspectedEntry: null,
      flashCardKey: null,
      // Windowing state mirrored up from CardGrid via onViewChange. Used to
      // compute the alphabet index's active letter and to translate an alphabet
      // jump (entry index → row). The grid owns the actual virtualization.
      cols: 0,
      rowHeight: 0,
      scrollTop: 0,
      highlightedEntryKey: null,
      // Bumped on every user-initiated filter/sort change; CardGrid animates a
      // content refresh when it changes.
      filterGen: 0,
      gridSize: normalizeGridSize(getPref(GRID_SIZE_PREF_KEY, 'auto')),
      sortBy: normalizeCardSort(getPref(CARD_SORT_PREF_KEY, 'name')),
      sortDir: normalizeCardSortDir(getPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC)),
      // Per-tile face state for two-sided cards (Druid avatar etc.).
      // Stored as a Set of card keys currently showing the back face.
      // missing key means front. Local preview only; flipping a tile
      // here doesn't affect the deck data or any other surface.
      flippedTiles: new Set(),
    };
    this.gridRef = createRef();
    this._highlightTimer = null;
    this.inspectorHold = new InspectorHoldTracker();
  }

  toggleTileFace = (key) => {
    this.setState((s) => {
      const next = new Set(s.flippedTiles);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { flippedTiles: next };
    });
  };

  setTileFace = (key, isBack) => {
    this.setState((s) => {
      const has = s.flippedTiles.has(key);
      if (has === isBack) return null;
      const next = new Set(s.flippedTiles);
      if (isBack) next.add(key);
      else next.delete(key);
      return { flippedTiles: next };
    });
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    // Deferred one macrotask: CardFilterBar emits its initial filter state from
    // its OWN componentDidMount, which runs before this one (children mount
    // first) but lands as a batched setState that flushes after. Reading
    // getEntries() synchronously here would preload the unfiltered pool's first
    // tiles rather than the ones the default scope. 'deck' for a deck that has
    // cards, 'owned' otherwise. Actually puts on screen.
    this._preloadTimer = setTimeout(this.preloadFirstScreen, 0);
  }

  /**
   * Warm the first screenful of card art, then tell the editor it may run its
   * entrance. Never rejects: every failure path resolves, because a missing
   * image must not trap the user behind the loading gate.
   */
  preloadFirstScreen = async () => {
    this._preloadTimer = null;
    const urls = this.getEntries()
      .slice(0, PRELOAD_TILE_COUNT)
      .map((entry) => getImageUrl(entry))
      .filter(Boolean);
    await preloadImages(urls, PRELOAD_TIMEOUT_MS);
    if (!this._unmounted) this.props.onReady?.();
  };

  componentDidUpdate(_prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    // Lets the user press Space first and find a card to inspect
    // afterward, without having to release and re-press.
    const prevHoverCard = prevState.hoveredCard?.card;
    const nowHoverCard = this.state.hoveredCard?.card;
    if (
      !this.state.inspectedEntry &&
      nowHoverCard &&
      nowHoverCard !== prevHoverCard &&
      this.state.hoveredCard?.printing &&
      this.inspectorHold.consumePending()
    ) {
      const { card, printing } = this.state.hoveredCard;
      this.setState({ inspectedEntry: { card, printing } });
    }

    const inspectorOpen = this.state.inspectedEntry != null;
    const wasInspectorOpen = prevState.inspectedEntry != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => this.setState({ inspectedEntry: null }));
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
    if (this._preloadTimer) {
      clearTimeout(this._preloadTimer);
      this._preloadTimer = null;
    }
    if (this._highlightTimer) {
      clearTimeout(this._highlightTimer);
      this._highlightTimer = null;
    }
  }

  // Mirror CardGrid's windowing state up so the alphabet index can compute its
  // active letter and translate an alphabet jump (entry index → row). Cheap
  // setState fired once per row crossing.
  handleViewChange = ({ scrollTop, cols, rowHeight }) => {
    if (
      this.state.scrollTop !== scrollTop ||
      this.state.cols !== cols ||
      this.state.rowHeight !== rowHeight
    ) {
      this.setState({ scrollTop, cols, rowHeight });
    }
  };

  // Called by every filter-mutating handler. Delegates to CardGrid to scroll
  // back to the top so the user sees the freshly filtered set from row 0, and
  // bumps filterGen so the grid eases the new result set in.
  resetScroll = () => {
    this.gridRef.current?.resetScroll();
    this.setState((s) => ({ filterGen: s.filterGen + 1 }));
  };

  isEditableTarget = (el) => {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName?.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select';
  };

  handleKeyDown = (e) => {
    if (this.isEditableTarget(e.target)) return;

    if ((e.key === ' ' || e.code === 'Space')) {
      e.preventDefault();
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) return;
      if (this.state.inspectedEntry) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredCard?.card && this.state.hoveredCard.printing) {
        const { card, printing } = this.state.hoveredCard;
        this.inspectorHold.noteOpen();
        this.setState({ inspectedEntry: { card, printing } });
      } else if (this.props.getSidebarHovered?.()) {
        // The press belongs to the deck sidebar's hovered row. Its own
        // handler opens the inspector. Arming here would ambush the user
        // with a second inspector the next time they hover a grid tile.
        return;
      } else {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
      return;
    }

    // Zone hotkeys. Add hovered card to a specific zone
    const hovered = this.state.hoveredCard;
    if (!hovered?.card) return;
    const { card, printing } = hovered;
    const isSite = card.type === 'Site' || card.played_horizontally;
    const key = e.key.toLowerCase();

    if (key === 'a' && isSite) {
      e.preventDefault();
      this.props.onAddCardToZone?.(card, 'atlas', printing);
    } else if (key === 's' && !isSite) {
      e.preventDefault();
      this.props.onAddCardToZone?.(card, 'spellbook', printing);
    } else if (key === 'c') {
      e.preventDefault();
      this.props.onAddCardToZone?.(card, 'collection', printing);
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    this.setState({ inspectedEntry: null });
  };

  // Server stores collection as (cardId, artGroup, foiling, quantity). Look
  // ownership up by that triple, not by printingId. Printings that share an
  // illustration collapse to one row on the server, so an Alpha and a Beta
  // booster copy are the same row while a Welcome Kit copy is its own.
  _getVariantOwnedMap() {
    const { collection } = this.props;
    if (this._cachedCollectionRef === collection && this._cachedVariantMap) {
      return this._cachedVariantMap;
    }
    this._cachedCollectionRef = collection;
    this._cachedVariantMap = buildVariantOwnedMap(collection || []);
    return this._cachedVariantMap;
  }

  // Memoized entry list. getFilteredCards() filters + sorts the whole card DB
  // and expands it into ~1k tile entries; CardGrid writes scrollTop back into
  // this component's state on every row crossing, so without this the entire
  // derivation re-ran mid-scroll AND handed CardGrid brand-new entry objects.
  // invalidating the per-tile meta cache and re-rendering every mounted tile.
  // Keyed on the inputs that actually change the list, so a scroll re-render
  // reuses the same array identity and costs nothing.
  getEntries() {
    const { sorceryCards, chosenCards, collection, ownedMap, gameMode } = this.props;
    const { matchesCard, matchesFoiling, filterScope, sortBy, sortDir } = this.state;
    const key = [
      sorceryCards, chosenCards, collection, ownedMap, gameMode,
      matchesCard, matchesFoiling, filterScope, sortBy, sortDir,
    ];
    if (this._entriesKey && key.every((v, i) => v === this._entriesKey[i])) {
      return this._entries;
    }
    this._entriesKey = key;
    this._entries = this.getFilteredCards();
    // Entry identities changed, so the derived per-tile state is stale.
    this._metaCache = new Map();
    this._letterMap = null;
    return this._entries;
  }

  getLetterIndex(entries) {
    if (this._letterMap && this._letterMapFor === entries) return this._letterMap;
    this._letterMapFor = entries;
    this._letterMap = this.buildLetterIndex(entries);
    return this._letterMap;
  }

  getFilteredCards() {
    const { sorceryCards, chosenCards } = this.props;
    const { matchesCard, matchesFoiling, filterScope } = this.state;

    // Card-level filters come from CardFilterBar (search, pills, advanced
    // panel, scope). Foiling / scope-driven tile expansion are local to
    // the deck editor because they affect how many tiles a card produces.
    const cards = (sorceryCards || []).filter(matchesCard);

    cards.sort(makeCardComparator(this.state.sortBy, undefined, this.state.sortDir));

    const variantOwned = this._getVariantOwnedMap();
    const entries = [];

    // Each card expands into one tile per (art group x foiling) it can be
    // owned as. An alt illustration is its own tile with its own foil and its
    // own count. Not a skin that displaces the booster art, which is what it
    // used to be. Deckbuilding limits are unaffected: they key on the card,
    // so four Apprentice Wizards still means four across every tile here.
    for (const card of cards) {
      const owned = variantOwned.get(card.unique_id) || new Map();

      for (const artGroup of artGroupsForCard(card)) {
        const printings = printingsInArtGroup(card, artGroup);
        if (printings.length === 0) continue;

        // For 'deck' scope, show only the variants actually in the deck.
        // Owning a foil copy of a card whose standard copy is in the deck
        // must not surface the foil under 'In Deck'.
        let scopedFoilings;
        if (filterScope === 'deck') {
          scopedFoilings = new Set((chosenCards || [])
            .filter((e) => e.card.unique_id === card.unique_id && artGroupOf(e.printing) === artGroup)
            .map((e) => e.printing?.foiling || 'S'));
        } else if (filterScope === 'all') {
          scopedFoilings = new Set(printings.map((p) => p.foiling || 'S'));
        } else {
          scopedFoilings = new Set(
            printings.map((p) => p.foiling || 'S')
              .filter((f) => (owned.get(variantKey(artGroup, f)) || 0) > 0)
          );
        }

        // Standard first so a card's plain tile leads its foil, matching the
        // order players read the grid in.
        for (const foiling of ['S', ...[...scopedFoilings].filter((f) => f !== 'S')]) {
          if (!scopedFoilings.has(foiling)) continue;
          const printing = printings.find((p) => (p.foiling || 'S') === foiling);
          if (!printing) continue;
          entries.push({
            card, printing, artGroup, foiling,
            ownedQty: owned.get(variantKey(artGroup, foiling)) || 0,
          });
        }
      }
    }

    return entries.filter((e) => matchesFoiling(e.foiling));
  }

  handleGridSizeChange = (value) => {
    const next = normalizeGridSize(value);
    if (next === this.state.gridSize) return;
    playUI(UI.HOVER, { volume: 0.5 });
    setPref(GRID_SIZE_PREF_KEY, next);
    // filterGen bump replays the grid's refresh animation. The column
    // change relays out every tile, so ease the new layout in.
    this.setState((s) => ({ gridSize: next, filterGen: s.filterGen + 1 }));
  };

  handleSortByChange = (value) => {
    playUI(UI.HOVER, { volume: 0.5 });
    const next = nextCardSort(this.state, value);
    if (!next) return;
    setPref(CARD_SORT_PREF_KEY, next.sortBy);
    setPref(CARD_SORT_DIR_PREF_KEY, next.sortDir);
    this.setState(next, this.resetScroll);
  };

  renderGridSizeSelector() {
    const { gridSize } = this.state;
    return (
      <div className="flex items-center gap-0.5" aria-label={t('Cards per row')}>
        <span
          className="text-[0.625cqw] mr-1.5"
          style={{ color: `${GOLD} 0.45)` }}
        >
          {t('Cols')}
        </span>
        {GRID_SIZE_OPTIONS.map((opt) => {
          const active = gridSize === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              className="filter-chip"
              data-active={active ? 'true' : 'false'}
              aria-label={t('Show {n} cards per row', { n: t(opt.label) })}
              aria-pressed={active}
              onClick={() => this.handleGridSizeChange(opt.value)}
            >
              {t(opt.label)}
            </button>
          );
        })}
      </div>
    );
  }

  handleFilterChange = ({ state, matchesCard, matchesFoiling, userInitiated }) => {
    // Only jump back to the top when the filter actually changed because
    // the user did something (typed, toggled a pill, switched scope).
    // Re-emits caused by prop churn. Most importantly scopeData
    // updating because chosenCards changed after adding/removing a card.
    // must NOT reset scroll, otherwise the grid snaps to the top every
    // time the player adds a card.
    this.setState(
      { matchesCard, matchesFoiling, filterScope: state.cardScope },
      userInitiated ? this.resetScroll : undefined,
    );
  };

  buildLetterIndex(entries) {
    const map = new Map();
    for (let i = 0; i < entries.length; i += 1) {
      const name = entries[i]?.card?.name || '';
      const ch = name.charAt(0).toUpperCase();
      if (ch >= 'A' && ch <= 'Z' && !map.has(ch)) map.set(ch, i);
    }
    return map;
  }

  handleJumpToIndex = (entryIndex) => {
    const { cols, rowHeight } = this.state;
    if (!cols || !rowHeight) return;
    const row = Math.floor(entryIndex / cols);
    this.gridRef.current?.scrollToRow(row);

    const entry = this._currentEntries?.[entryIndex];
    if (entry?.card) {
      const { card, artGroup, foiling } = this._entryBasics(entry);
      const key = entryKey(card.unique_id, artGroup, foiling);
      if (this._highlightTimer) clearTimeout(this._highlightTimer);
      if (this.state.highlightedEntryKey !== key) {
        this.setState({ highlightedEntryKey: key });
      }
      this._highlightTimer = setTimeout(() => {
        this._highlightTimer = null;
        this.setState({ highlightedEntryKey: null });
      }, 900);
    }
  };

  // Stable scopeData reference. The filter bar re-emits when props
  // change, so a fresh object every render would spin an infinite loop
  // (emit → setState → re-render → new scopeData → emit). Only rebuild
  // when the inputs that actually feed scope filtering change.
  _getScopeData() {
    const { chosenCards, ownedMap } = this.props;
    if (this._scopeChosenCards === chosenCards && this._scopeOwnedMap === ownedMap) {
      return this._scopeData;
    }
    this._scopeChosenCards = chosenCards;
    this._scopeOwnedMap = ownedMap;
    this._scopeData = {
      deckCardIds: new Set((chosenCards || []).map((e) => e.card.unique_id)),
      ownedMap,
    };
    return this._scopeData;
  }


  renderRarityCapacityDots(maxCopies, inDeck, flash = false) {
    const filled = { opacity: 1, filter: 'sepia(1) saturate(5) brightness(1.2) hue-rotate(15deg)', dropShadow: `0 0 3px ${ACCENT_GOLD}` };
    const empty = { opacity: 0.6, filter: 'sepia(1) saturate(2) brightness(1.0) hue-rotate(15deg)' };

    // Cards that print "may include any number" (Grey Wolves, basic
    // sites) have no copy cap, so a fixed dot row can't represent them.
    // and Array.from({ length: Infinity }) would throw. Show an "∞" glyph
    // instead, lit gold once at least one copy is in the deck.
    if (!Number.isFinite(maxCopies)) {
      return (
        <motion.div
          className="flex items-center justify-center h-4 font-bold leading-none tabular-nums"
          style={{
            color: ACCENT_GOLD,
            fontSize: PHONE ? '0.9rem' : '1.1cqw',
            opacity: inDeck > 0 ? 1 : 0.6,
            textShadow: inDeck > 0 ? `0 0 3px ${ACCENT_GOLD}` : 'none',
          }}
          animate={flash ? { scale: [1, 1.15, 1, 1.1, 1], x: [0, -2, 2, -1, 0] } : { scale: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          title={t('No copy limit. Your deck may include any number')}
        >
          {inDeck > 0 ? `${inDeck} ×∞` : '∞'}
        </motion.div>
      );
    }

    return (
      <motion.div
        className="flex items-center justify-center gap-1 h-4"
        animate={flash ? { scale: [1, 1.15, 1, 1.1, 1], x: [0, -2, 2, -1, 0] } : { scale: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        {Array.from({ length: maxCopies }, (_, i) => {
          const active = i < inDeck;
          return (
            <div
              key={i}
              style={{
                width: PHONE ? '0.875rem' : '1cqw',
                height: PHONE ? '0.875rem' : '1cqw',
                borderRadius: 'var(--radius-sm)',
                outline: active ? 'none' : `1px solid rgba(180,150,80,0.3)`,
                filter: active ? `drop-shadow(${filled.dropShadow})` : undefined,
              }}
            >
              <img
                src="/rune-divider.webp"
                alt=""
                draggable={false}
                style={active
                  ? { width: '100%', height: '100%', opacity: filled.opacity, filter: filled.filter }
                  : { width: '100%', height: '100%', opacity: empty.opacity, filter: empty.filter }
                }
              />
            </div>
          );
        })}
      </motion.div>
    );
  }

  // Stable key for a collection entry. `${cardId}-${artGroup}-${foiling}`.
  // The grid keys tiles by this; the inspector mirrors it (see render()). The
  // art group is load-bearing: without it a card's booster foil and its
  // alt-art foil collide on one key and the grid renders only one of them.
  getItemKey = (entry) => {
    const { card, artGroup, foiling } = this._entryBasics(entry);
    return entryKey(card.unique_id, artGroup, foiling);
  };

  // Foil descriptor for CardGrid: foil entries render their art in the shared
  // WebGL canvas (imageHidden on the DOM tile); non-foil return null and keep
  // their DOM <img>.
  getItemFoil = (entry) => {
    const { printing, artGroup, foiling, card } = this._entryBasics(entry);
    if (!isFoilFinish(foiling)) return null;
    // Foil tiles draw in the shared WebGL scene (imageHidden DOM tile), so the
    // descriptor must track the flipped face itself. Two-sided foils (Druid)
    // won't flip otherwise. Mirror the DOM tile's front/back URL resolution.
    const key = entryKey(card.unique_id, artGroup, foiling);
    const face = card.backFace && this.state.flippedTiles.has(key) ? 'back' : 'front';
    return { imageUrl: getImageUrl({ card, printing }, face) || '', zones: foilZonesForCard(card) };
  };

  // Per-cell wrapper style CardGrid merges onto the absolutely-positioned cell:
  // the unowned graying (applied to both tile + footer cells in the legacy grid)
  // plus the hover / alphabet-highlight zIndex lift (tile cell only. CardGrid
  // strips zIndex from the footer cell). These are deck-builder business state,
  // so they live here rather than in the generic grid.
  getItemCellStyle = (entry) => {
    const m = this._entryMeta(entry);
    const isLetterHighlighted = this.state.highlightedEntryKey === m.key;
    const style = m.unowned ? { opacity: 0.55, filter: 'grayscale(0.5) brightness(0.8)' } : {};
    // The hover lift is CSS (`.card-grid > *:hover`), not state: driving it
    // from `hoveredCard` re-rendered every mounted tile on each mouse-enter,
    // which is exactly when the user is also scrolling. Only the alphabet
    // highlight. A deliberate, occasional action. Lifts from state here.
    // Both stay below the search-bar dropdowns (autocomplete z-40).
    if (isLetterHighlighted) style.zIndex = 11;
    return style;
  };

  // Normalize an entry to { card, printing, artGroup, foiling }. Supports
  // both the expanded format getFilteredCards emits and a bare card
  // (defensive).
  _entryBasics(entry) {
    const isExpanded = entry.card && entry.printing && entry.foiling !== undefined;
    const card = isExpanded ? entry.card : entry;
    const printing = isExpanded ? entry.printing : (card.printings?.[card.printings.length - 1] || card.printings?.[0]);
    const foiling = isExpanded ? entry.foiling : (printing?.foiling || 'S');
    const artGroup = isExpanded ? entry.artGroup : artGroupOf(printing);
    return { card, printing, artGroup, foiling, isExpanded };
  }

  // Derive all per-tile deck-builder state for an entry (ownership, caps, avatar
  // rules, click availability). Memoized per render keyed by the entry object so
  // renderTile + renderFooter + getItemCellStyle share one computation (the
  // cache is invalidated in render() when the entry list changes).
  _entryMeta(entry) {
    if (!this._metaCache) this._metaCache = new Map();
    const cached = this._metaCache.get(entry);
    if (cached) return cached;

    const { ownedMap, chosenCards } = this.props;
    const { card, printing, artGroup, foiling, isExpanded } = this._entryBasics(entry);
    const isFoilEntry = isFoilFinish(foiling);

    // Per-variant ownership comes from the expanded entry. The server stores
    // collection by (cardId, artGroup, foiling), so this value is
    // authoritative for this exact art in this exact finish.
    const printingOwnedQty = isExpanded ? entry.ownedQty : (ownedMap?.get(card.unique_id) || 0);
    const inDeckWithPrinting = (chosenCards || []).filter((e) =>
      e.card.unique_id === card.unique_id
      && artGroupOf(e.printing) === artGroup
      && (e.printing?.foiling || 'S') === foiling
    ).length;
    const totalInCurrentDeck = (chosenCards || []).filter((e) =>
      e.card.unique_id === card.unique_id
    ).length;
    const totalOwned = ownedMap?.get(card.unique_id) || 0;
    // Identify the primary avatar by the avatar slot, not just by type.
    // Imposter decks may carry additional avatars in spellbook/collection, and
    // grabbing one of those would apply the wrong rules to every other card.
    const avatar =
      chosenCards.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
      || chosenCards.find((e) => e.card?.type === 'Avatar' && !e.zone)?.card
      || null;
    const maxCopies = getMaxCopies(card, { avatar });
    // Suppress the rune-ornament copy indicator for cards the active avatar
    // categorically bans (Duplicator hides it on every non-Unique rarity).
    const bannedByAvatar = isCardBannedByAvatar(card, avatar);
    const remainingByLimitRaw = getRemainingCopies(card, chosenCards, { avatar });
    // Imposter carve-out: additional Avatar cards land in the spellbook (or
    // collection via the C hotkey) and aren't subject to the singleton cap.
    const allowsExtraAvatar =
      card.type === 'Avatar'
      && resolveAvatarRules(avatar).deckbuilding.allowExtraAvatars;
    const remainingByLimit = allowsExtraAvatar ? Infinity : remainingByLimitRaw;
    const remainingByOwnership = totalOwned - totalInCurrentDeck;
    // Sandbox decks and free play ignore ownership; only the copy limits hold.
    const isSandbox = this.props.gameMode === 'sandbox' || isFreePlay();
    const available = isSandbox
      ? remainingByLimit
      : Math.min(printingOwnedQty - inDeckWithPrinting, remainingByOwnership, remainingByLimit);
    const unowned = !isSandbox && printingOwnedQty === 0 && inDeckWithPrinting === 0;
    const key = entryKey(card.unique_id, artGroup, foiling);

    const meta = {
      card, printing, artGroup, foiling, isFoilEntry, key,
      printingOwnedQty, inDeckWithPrinting, maxCopies, bannedByAvatar,
      remainingByLimit, available, unowned,
    };
    this._metaCache.set(entry, meta);
    return meta;
  }

  renderTile = (entry, { imageHidden, onTilt }) => {
    const m = this._entryMeta(entry);
    const { card, printing, foiling, key, isFoilEntry } = m;
    const isLetterHighlighted = this.state.highlightedEntryKey === key;
    return (
      <div
        onPointerDown={this.props.onTilePress ? (e) => this.props.onTilePress(e, card, printing) : undefined}
        style={{
          borderRadius: 'var(--radius-xl)',
          boxShadow: isLetterHighlighted
            ? `0 0 0 2px ${ACCENT_GOLD}, 0 0 22px 4px rgba(232,195,110,0.55)`
            : '0 0 0 0 rgba(0,0,0,0)',
          transition: 'box-shadow 0.35s ease, transform 0.2s ease',
          transform: isLetterHighlighted ? 'scale(1.03)' : 'scale(1)',
          transformOrigin: 'center center',
        }}
      >
        <DeckCardTile
          entry={{ card, printing: printing || {}, zone: 'spellbook', entryIndex: 0 }}
          isSelected={false}
          imageHidden={imageHidden}
          onTilt={onTilt}
          face={this.state.flippedTiles.has(key) ? 'back' : 'front'}
          onClick={() => {
            if (m.unowned) return;
            if (m.available <= 0) {
              let reason;
              if (m.remainingByLimit <= 0) {
                const rarity = card.rarity ? t(card.rarity) : t('This');
                reason = card.type === 'Avatar'
                  ? t('Only one Avatar is allowed per deck')
                  : (m.maxCopies === 1
                    ? t('{rarity} cards are limited to 1 copy per deck', { rarity })
                    : t('{rarity} cards are limited to {n} copies per deck', { rarity, n: m.maxCopies }));
              } else {
                const label = [artGroupLabel(m.artGroup), isFoilEntry ? FOIL_LABEL[foiling] : '']
                  .filter(Boolean).join(', ');
                const name = `${artGroupDisplayName(card, m.artGroup)}${label ? ` (${label})` : ''}`;
                reason = m.printingOwnedQty === 1
                  ? t('All 1 owned copy of {name} already in this deck', { name })
                  : t('All {n} owned copies of {name} already in this deck', { n: m.printingOwnedQty, name });
              }
              playUI(UI.ERROR);
              this.props.onShowToast?.(reason);
              this.setState({ flashCardKey: key });
              setTimeout(() => this.setState({ flashCardKey: null }), 600);
              return;
            }
            playUI(UI.EQUIP);
            this.props.onAddCard?.(card, printing);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            if (m.inDeckWithPrinting > 0) {
              playUI(UI.UNEQUIP);
              this.props.onRemoveCard?.(card.unique_id, m.artGroup, foiling);
            }
          }}
          onHoverChange={(hovered) => this.setState({ hoveredCard: hovered ? { key, card, printing } : null })}
        />
      </div>
    );
  };

  renderFooter = (entry) => {
    const m = this._entryMeta(entry);
    const { card, artGroup, foiling, key, isFoilEntry, printingOwnedQty, maxCopies, bannedByAvatar, inDeckWithPrinting } = m;
    // Two tiles of one card differ only by art, so the alt tile has to say
    // which printing it is. Most alt arts reuse the card's name.
    const groupLabel = artGroupLabel(artGroup);
    const qualifier = [groupLabel, isFoilEntry ? (FOIL_LABEL[foiling] || 'Foil') : '']
      .filter(Boolean).join(', ');
    return (
      <>
        <div className="relative mt-1.5 h-4 flex items-center justify-center">
          {/* Owned-copies badge, moved off the card into the footer row (left of
              the centered capacity dots) so it no longer has to track the WebGL
              card through its hover pivot/flip. */}
          <div className="absolute flex items-center" style={{ left: 0, top: '50%', transform: 'translateY(-50%)' }}>
            {isFreePlay() ? null : (
              <CardOwnedBadge
                owned={printingOwnedQty}
                maxCopies={maxCopies}
                playsetCompleteVariant={false}
                hideWhenZero
                inline
              />
            )}
          </div>
          {!bannedByAvatar && this.renderRarityCapacityDots(maxCopies, inDeckWithPrinting, this.state.flashCardKey === key)}
          {card.backFace ? (
            <div
              className="absolute flex items-center"
              style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }}
            >
              <CardFlipButton
                position="inline"
                face={this.state.flippedTiles.has(key) ? 'back' : 'front'}
                onFlip={() => this.toggleTileFace(key)}
                size={PHONE ? '1.5rem' : '1.4cqw'}
              />
            </div>
          ) : null}
        </div>
        <div
          className="text-center mt-0.5 truncate px-1"
          style={{ fontSize: PHONE ? '0.625rem' : '0.625cqw', color: isFoilEntry ? (foiling === 'R' ? '#d98eff' : '#6dd5ed') : TEXT_MUTED, lineHeight: 1.3 }}
        >
          {artGroupDisplayName(card, artGroup)}{qualifier ? ` (${qualifier})` : ''}
        </div>
      </>
    );
  };

  render() {
    const cards = this.getEntries();
    const { inspectedEntry, scrollTop, cols, rowHeight } = this.state;
    const { sorceryCards, chosenCards } = this.props;
    if (!this._initialState) {
      this._initialState = { cardScope: defaultCardScope(chosenCards) };
    }

    this._currentEntries = cards;
    // Alphabet jump only makes sense when entries are in name order;
    // hide the column when sorting by cost so the user isn't jumping to
    // a letter that isn't sitting at a predictable position anymore.
    const showAlphabetIndex = !PHONE && this.state.sortBy === 'name';
    const letterMap = showAlphabetIndex ? this.getLetterIndex(cards) : new Map();
    let activeLetter = null;
    if (showAlphabetIndex && cols > 0 && rowHeight > 0 && cards.length > 0) {
      const topRow = Math.max(0, Math.floor(scrollTop / rowHeight));
      const topEntryIdx = Math.min(cards.length - 1, topRow * cols);
      const ch = (cards[topEntryIdx]?.card?.name || '').charAt(0).toUpperCase();
      if (ch >= 'A' && ch <= 'Z') activeLetter = ch;
    }

    // The cascade is held until the editor says the first screenful of art is
    // warm (entranceReady). Applying the classes before that would burn the
    // animation while the content is still hidden behind the loading gate, and
    // the reveal would then be an instant pop.
    const { entranceReady } = this.props;

    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Editor-open choreography: filter bar rises in first, the tile
            cascade follows, and the sidebar (staggered by DeckEditor)
            lands alongside. Same assembly language as the confirm
            dialogs (.stagger-in / --stagger). */}
        <CardFilterBar
          className={entranceReady ? 'stagger-in mb-3' : 'mb-3'}
          style={entranceReady ? { '--stagger': '70ms' } : undefined}
          portalTarget={this.props.filterSlot}
          sorceryCards={sorceryCards}
          features={{ scope: true, finish: true, type: true }}
          initialState={this._initialState}
          scopeData={this._getScopeData()}
          onChange={this.handleFilterChange}
          bottomTrailing={(
            <div className="flex items-center gap-3">
              <CardSortChips sortBy={this.state.sortBy} sortDir={this.state.sortDir} onSelect={this.handleSortByChange} />
              {!PHONE && this.renderGridSizeSelector()}
            </div>
          )}
        />
        <div className="flex-1 flex min-h-0 relative">
          {/* CardGrid stays mounted across filter changes (incl. transient empty
              results) so its WebGL foil context isn't torn down and rebuilt. The
              empty message overlays it when nothing matches. */}
          <CardGrid
            ref={this.gridRef}
            items={cards}
            entranceStagger={140}
            entranceReady={entranceReady}
            refreshKey={this.state.filterGen}
            gridSize={PHONE ? 'auto' : this.state.gridSize}
            getItemKey={this.getItemKey}
            getItemFoil={this.getItemFoil}
            getItemCellStyle={this.getItemCellStyle}
            renderTile={this.renderTile}
            renderFooter={this.renderFooter}
            onViewChange={this.handleViewChange}
            // Mirror the footer (dots row + name row) in the probe so the
            // measured row height includes this surface's real footer height.
            measureFooter={(
              <>
                <div className="relative mt-1.5 h-4" />
                <div className="text-center mt-0.5" style={{ fontSize: PHONE ? '0.625rem' : '0.625cqw', lineHeight: 1.3 }}>&nbsp;</div>
              </>
            )}
          />
          {cards.length === 0 ? (
            <div className="card-grid-empty-msg absolute inset-0 flex items-center justify-center py-12" style={{ color: TEXT_MUTED }}>
              <span className="text-sm">{t('No cards match your filters.')}</span>
            </div>
          ) : null}
          {showAlphabetIndex ? (
            <AlphabetScrollIndex
              letterMap={letterMap}
              activeLetter={activeLetter}
              onJumpToIndex={this.handleJumpToIndex}
            />
          ) : null}
        </div>
        {inspectedEntry ? (() => {
          // The grid keys tiles by `${cardId}-${artGroup}-${foiling}`. Mirror
          // that here so the inspector seeds from the same `flippedTiles`
          // entry, and so flipping inside the inspector writes back to
          // the same key. Keeping the grid tile and inspector in lock
          // step while the modal is open.
          const inspectedKey = entryKey(
            inspectedEntry.card.unique_id,
            artGroupOf(inspectedEntry.printing),
            inspectedEntry.printing?.foiling,
          );
          const inspectedFace = this.state.flippedTiles.has(inspectedKey) ? 'back' : 'front';
          return (
            <CardInspector
              card={inspectedEntry.card}
              imageUrl={inspectedEntry.printing?.image_url}
              rarity={inspectedEntry.card?.rarity}
              foiling={inspectedEntry.printing?.foiling}
              initialFace={inspectedFace}
              onFlipChange={(face) => this.setTileFace(inspectedKey, face === 'back')}
              onClose={() => this.setState({ inspectedEntry: null })}
            />
          );
        })() : null}
      </div>
    );
  }
}
